import { Controller } from 'stimulus';

export default class extends Controller {
  static targets = ["entries", "pagination", "loading"]

  initialize() {
    this.isLoading = false
    this.pagesLoadedInBatch = 0

    let options = {
      rootMargin: '200px',
    }

    this.intersectionObserver = new IntersectionObserver(entries => this.processIntersectionEntries(entries), options)
  }

  get autoLoadUntil() {
    return parseInt(this.data.get('autoLoadUntil')) || 0
  }

  get batchSize() {
    return parseInt(this.data.get('batchSize')) || 1
  }

  connect() {
    this.intersectionObserver.observe(this.paginationTarget)

    // Auto-load pages until we reach autoLoadUntil threshold
    if (this.autoLoadUntil > 0) {
      this.autoLoadInitial()
    }
  }

  disconnect() {
    this.intersectionObserver.unobserve(this.paginationTarget)
  }

  autoLoadInitial() {
    const currentCount = this.entriesTarget.children.length
    if (currentCount < this.autoLoadUntil && this.hasNextPage()) {
      this.loadPage(() => {
        setTimeout(() => this.autoLoadInitial(), 100)
      })
    }
  }

  processIntersectionEntries(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting && !this.isLoading) {
        this.pagesLoadedInBatch = 0
        this.loadBatch()
      }
    })
  }

  loadBatch() {
    if (this.pagesLoadedInBatch < this.batchSize && this.hasNextPage()) {
      this.loadPage(() => {
        this.pagesLoadedInBatch++
        setTimeout(() => this.loadBatch(), 100)
      })
    }
  }

  hasNextPage() {
    return !!this.paginationTarget.querySelector("a[rel='next']")
  }

  loadPage(callback) {
    if (this.isLoading) return

    let next_page = this.paginationTarget.querySelector("a[rel='next']")
    if (!next_page) {
      this.paginationTarget.classList.add("hidden")
      return
    }

    let url = next_page.href
    this.showLoading()
    this.isLoading = true

    Rails.ajax({
      type: 'GET',
      url: url,
      dataType: 'json',
      success: (data) => {
        this.appendEntries(data.entries)
        this.paginationTarget.innerHTML = data.pagination
        this.hideLoading()
        this.isLoading = false
        if (callback) callback()
      },
      error: () => {
        this.hideLoading()
        this.isLoading = false
      }
    })
  }

  // Append a page of entries, skipping any whose data-infinite-scroll-key is
  // already in the list. Offset pagination can re-serve a row when records are
  // created/reordered between page fetches, and a plain insertAdjacentHTML
  // would then render the same entry twice. Entries without the attribute are
  // appended unconditionally (index pages that don't set a key keep the old
  // behavior). Parsing goes through a <template> so <tr> entries survive
  // outside a <table> context.
  appendEntries(html) {
    const template = document.createElement('template')
    template.innerHTML = html
    const keyed = template.content.querySelectorAll('[data-infinite-scroll-key]')
    if (keyed.length > 0) {
      const seen = new Set(
        Array.from(this.entriesTarget.querySelectorAll('[data-infinite-scroll-key]'))
          .map(el => el.getAttribute('data-infinite-scroll-key'))
      )
      keyed.forEach(el => {
        if (seen.has(el.getAttribute('data-infinite-scroll-key'))) el.remove()
      })
    }
    this.entriesTarget.appendChild(template.content)
  }

  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove("hidden")
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add("hidden")
    }
  }
}